import axios from "axios";

export const WP_BASE_URL = "https://backend.cricbuddy.in/wp-json/wp/v2";

const DEFAULT_FALLBACK_IMAGES = [
  "/images/VideoPlayImage.jpg",
  "/images/PackageBanner_2.png",
  "/images/ListVenueBanner.png",
  "/images/FindPlayer.png",
  "/images/Homepagebanner.png",
  "/images/AuditoriumBaner_2.png",
];

/**
 * Helper to decode HTML entities (e.g. &#8211;, &amp;, &nbsp;)
 */
export const decodeHtml = (htmlString) => {
  if (!htmlString) return "";
  const doc = new DOMParser().parseFromString(htmlString, "text/html");
  return doc.body.textContent || "";
};

/**
 * Helper to strip HTML tags for clean preview text
 */
export const stripHtmlTags = (htmlString) => {
  if (!htmlString) return "";
  const text = decodeHtml(htmlString);
  return text.replace(/\s+/g, " ").trim();
};

/**
 * Format ISO date string into human readable date
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch (e) {
    return dateStr;
  }
};

/**
 * Transform WordPress REST API post item into application format
 */
export const transformWpPost = (item, index = 0) => {
  if (!item) return null;

  const title = decodeHtml(item.title?.rendered || "Untitled Post");
  const rawExcerpt = item.excerpt?.rendered || item.content?.rendered || "";
  const summary = stripHtmlTags(rawExcerpt);
  const content = item.content?.rendered || "";
  const date = formatDate(item.date);

  // Extract featured media from _embedded if available
  const featuredMedia = item._embedded?.["wp:featuredmedia"]?.[0];
  const image =
    featuredMedia?.source_url ||
    featuredMedia?.media_details?.sizes?.large?.source_url ||
    DEFAULT_FALLBACK_IMAGES[index % DEFAULT_FALLBACK_IMAGES.length];

  // Extract categories from _embedded terms
  const termCategories = item._embedded?.["wp:term"]?.[0] || [];
  const categories =
    termCategories.length > 0
      ? termCategories.map((term) => term.name)
      : ["General"];

  const authorObj = item._embedded?.author?.[0];
  const author = authorObj?.name || "Cricbuddy";
  const authorAvatar =
    authorObj?.avatar_urls?.["96"] ||
    authorObj?.avatar_urls?.["48"] ||
    "https://secure.gravatar.com/avatar/?s=96&d=mm&r=g";

  // Extract ACF metadata fields
  const metaTitle = item.acf?.meta_title || "";
  const metaDescription = item.acf?.meta_description || "";
  const metaImage = item.acf?.meta_image?.url || "";

  return {
    id: item.id,
    slug: item.slug,
    link: item.link,
    title,
    summary,
    content,
    date,
    image,
    category: categories,
    author,
    authorAvatar,
    metaTitle,
    metaDescription,
    metaImage,
    rawItem: item,
  };
};

/**
 * Fetch listing of blog posts from WordPress REST API
 * GET /wp-json/wp/v2/posts?_embed
 */
export const getBlogPosts = async ({
  page = 1,
  perPage = 100,
  categoryId = null,
  search = "",
} = {}) => {
  try {
    const params = {
      _embed: true,
      page,
      per_page: perPage,
      acf_format: "standard",
    };

    if (categoryId) {
      params.categories = categoryId;
    }

    if (search && search.trim()) {
      params.search = search.trim();
    }

    const response = await axios.get(`${WP_BASE_URL}/posts`, { params });
    const posts = Array.isArray(response.data)
      ? response.data.map(transformWpPost)
      : [];

    return {
      posts,
      totalPages: parseInt(response.headers["x-wp-totalpages"] || "1", 10),
      totalPosts: parseInt(response.headers["x-wp-total"] || `${posts.length}`, 10),
    };
  } catch (error) {
    console.error("Error in getBlogPosts service:", error);
    throw error;
  }
};

/**
 * Fetch single blog post details by Post ID
 * GET /wp-json/wp/v2/posts/:id?_embed
 */
export const getBlogPostById = async (postId) => {
  try {
    const response = await axios.get(`${WP_BASE_URL}/posts/${postId}`, {
      params: { _embed: true, acf_format: "standard" },
    });
    return transformWpPost(response.data);
  } catch (error) {
    console.error(`Error fetching post ID ${postId}:`, error);
    throw error;
  }
};

/**
 * Fetch single blog post details by Slug
 * GET /wp-json/wp/v2/posts?slug=:slug&_embed
 */
export const getBlogPostBySlug = async (slug) => {
  try {
    const response = await axios.get(`${WP_BASE_URL}/posts`, {
      params: { slug, _embed: true, acf_format: "standard" },
    });
    if (Array.isArray(response.data) && response.data.length > 0) {
      return transformWpPost(response.data[0]);
    }
    return null;
  } catch (error) {
    console.error(`Error fetching post with slug ${slug}:`, error);
    throw error;
  }
};

/**
 * Fetch blog categories list
 * GET /wp-json/wp/v2/categories
 */
export const getBlogCategories = async () => {
  try {
    const response = await axios.get(`${WP_BASE_URL}/categories`, {
      params: { per_page: 100 },
    });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error("Error fetching categories service:", error);
    return [];
  }
};

export const getMediaUrl = async (mediaId) => {
  if (!mediaId) return null;

  try {
    const response = await axios.get(`${WP_BASE_URL}/media/${mediaId}`);
    return response.data?.source_url || null;
  } catch (error) {
    console.error(`Error fetching media ID ${mediaId}:`, error);
    return null;
  }
};

const blogService = {
  getBlogPosts,
  getBlogPostById,
  getBlogPostBySlug,
  getBlogCategories,
  transformWpPost,
  getMediaUrl,
};

export default blogService;
